import { useEffect, useRef, useState } from "react";
import { usePageMeta } from "@/components/pepmuse/usePageMeta";
import { useSearchParams } from "react-router-dom";
import {
  ScanSearch,
  CheckCircle2,
  Download,
  FileJson,
  RotateCcw,
  ArrowRight,
  ShieldAlert,
} from "lucide-react";
import { Reveal, Eyebrow } from "@/components/pepmuse/Reveal";
import { PrimaryButton } from "@/components/pepmuse/Buttons";
import { ClosingCTA } from "@/components/pepmuse/ClosingCTA";
import { formatApiErrorDetail } from "@/components/pepmuse/AuthContext";
import { exportCompliancePdf } from "@/components/pepmuse/pdf";

const API = process.env.REACT_APP_BACKEND_URL;

const SEVERITY = {
  critical: { label: "Critical", color: "#F87171" },
  high: { label: "High", color: "#F0B28A" },
  medium: { label: "Medium", color: "#E8CE8F" },
  low: { label: "Low", color: "#8FBFE8" },
};
const GRADE_COLORS = { A: "#8FD8B8", B: "#B8A7E8", C: "#E8CE8F", D: "#F0B28A", F: "#F87171" };
const GRADE_LABELS = {
  A: "Strong research-use signals",
  B: "Mostly research-use aligned",
  C: "Needs attention",
  D: "High-risk language",
  F: "Critical language risks",
};
const LOADING_STEPS = ["Fetching page…", "Reading content…", "Checking 15 rule sets…", "Scoring…"];

const fieldClass =
  "w-full rounded-xl border border-white/10 bg-ink/70 px-4 py-3 text-sm text-paper placeholder:text-paper/35 transition-[border-color,box-shadow] duration-200 focus:border-lilac/60 focus:outline-none focus:ring-2 focus:ring-lilac/30";

const ScoreRing = ({ score, grade }) => {
  const C = 2 * Math.PI * 52;
  const color = GRADE_COLORS[grade] || "#B8A7E8";
  return (
    <div className="relative h-36 w-36" data-testid="score-ring">
      <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
        <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(246,243,252,0.08)" strokeWidth="10" />
        <circle
          cx="60"
          cy="60"
          r="52"
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={C - (C * score) / 100}
          style={{ transition: "stroke-dashoffset 0.9s cubic-bezier(0.22,1,0.36,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-display text-4xl font-extrabold text-paper" data-testid="score-value">
          {score}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color }}>
          Grade {grade}
        </span>
      </div>
    </div>
  );
};

export default function ComplianceChecker({ onBuild }) {
  const [params] = useSearchParams();
  const [mode, setMode] = useState("url");
  const [url, setUrl] = useState(params.get("url") || "");
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(0);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const autoRan = useRef(false);

  usePageMeta(
    "Compliance Checker — PepMuse",
    "Free tool: scan your research peptide brand website for risky language and get a compliance score with flagged issues."
  );

  useEffect(() => {
    const shared = params.get("url");
    if (shared && !autoRan.current) {
      autoRan.current = true;
      runScan(shared, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runScan = async (targetUrl, targetHtml) => {
    setBusy(true);
    setError("");
    setResult(null);
    setStage(0);
    const stageTimer = setInterval(() => setStage((s) => Math.min(s + 1, LOADING_STEPS.length - 1)), 900);
    try {
      const res = await fetch(`${API}/api/compliance/scan`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: targetUrl, html: targetHtml }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(formatApiErrorDetail(data.detail));
        return;
      }
      setResult(data);
      setTimeout(() => {
        document.getElementById("scan-results")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch {
      setError("Couldn't reach the scanner. Please try again.");
    } finally {
      clearInterval(stageTimer);
      setBusy(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    runScan(mode === "url" ? url : "", mode === "html" ? html : "");
  };

  const flagCount = result ? result.flags.length : 0;

  return (
    <>
      <section data-testid="checker-hero" className="relative overflow-hidden pb-10 pt-[120px] sm:pt-[150px]">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -left-32 top-10 h-80 w-80 rounded-full bg-lilac/[0.07] blur-3xl" />
          <div className="absolute -right-24 bottom-0 h-72 w-72 rounded-full bg-lilac-deep/[0.08] blur-3xl" />
        </div>
        <div className="relative mx-auto w-full max-w-shell px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <Eyebrow testId="checker-eyebrow">Compliance checker · Free tool</Eyebrow>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-paper sm:text-5xl">
              Is your site's language research-use ready?
            </h1>
            <p className="mt-5 max-w-lg text-base text-paper/65 sm:text-lg">
              Scan any peptide website for language and imagery that signals non-research use. You get a weighted
              score, itemized risks, and the fix for every flag.
            </p>
            <p className="mt-5 max-w-lg text-sm leading-relaxed text-paper/40">
              A limited automated review of on-page language — it flags common risk patterns and can miss issues.
              It is not legal advice or a compliance determination; have qualified counsel review your site before
              launch.
            </p>
          </Reveal>
        </div>
      </section>

      <section id="scan" data-testid="checker-scan" className="scroll-mt-24 pb-16" tabIndex={-1}>
        <div className="mx-auto w-full max-w-shell px-5 sm:px-8">
          <Reveal>
            <form
              onSubmit={submit}
              className="rounded-[2rem] border border-lilac/25 bg-gradient-to-b from-lilac/[0.09] to-card p-7 sm:p-9"
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-paper/45">Scan a site</p>
                <div className="flex gap-1 rounded-full bg-ink/70 p-1" role="tablist" aria-label="Scan input type">
                  {[
                    { id: "url", label: "By URL" },
                    { id: "html", label: "Paste HTML" },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      role="tab"
                      aria-selected={mode === tab.id}
                      data-testid={`scan-mode-${tab.id}`}
                      onClick={() => setMode(tab.id)}
                      className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac ${
                        mode === tab.id ? "bg-lilac text-ink" : "text-paper/55 hover:text-paper"
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                {mode === "url" ? (
                  <input
                    data-testid="scan-url-input"
                    type="text"
                    inputMode="url"
                    value={url}
                    onChange={(e) => {
                      setUrl(e.target.value);
                      setError("");
                    }}
                    placeholder="yourstore.com"
                    className={fieldClass}
                    aria-label="Website URL to scan"
                  />
                ) : (
                  <textarea
                    data-testid="scan-html-input"
                    rows={7}
                    value={html}
                    onChange={(e) => {
                      setHtml(e.target.value);
                      setError("");
                    }}
                    placeholder="Paste the page HTML here (for bot-protected or login-only sites)…"
                    className={`${fieldClass} resize-none font-mono text-xs`}
                    aria-label="Page HTML to scan"
                  />
                )}
              </div>

              {error && (
                <p role="alert" data-testid="scan-error" className="mt-4 rounded-xl border border-red-400/30 bg-red-400/10 p-3.5 text-sm text-red-200">
                  {error}
                </p>
              )}

              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3">
                <PrimaryButton testId="scan-submit-button" type="submit" className={busy ? "pointer-events-none opacity-60" : ""}>
                  {busy ? LOADING_STEPS[stage] : "Scan site"}
                </PrimaryButton>
                <p className="text-xs text-paper/40">
                  Reads text, headings, links, meta tags, and image alt text. 15 rule sets, tuned for research-use
                  compliance.
                </p>
              </div>
            </form>
          </Reveal>

          {result && (
            <div id="scan-results" data-testid="scan-results" className="mt-12 scroll-mt-28" tabIndex={-1}>
              <Reveal>
                <div className="rounded-[2rem] border border-white/10 bg-card p-7 sm:p-10">
                  <div className="flex flex-col items-start gap-8 sm:flex-row sm:items-center">
                    <ScoreRing score={result.score} grade={result.grade} />
                    <div className="min-w-0">
                      <p className="text-xs font-bold uppercase tracking-[0.22em] text-paper/45">Scan report</p>
                      <h2 className="mt-2 font-display text-2xl font-extrabold text-paper sm:text-3xl">
                        {GRADE_LABELS[result.grade]}
                      </h2>
                      <p data-testid="scan-target" className="mt-2 max-w-md truncate text-sm text-paper/55">
                        {result.target} · {result.pages_scanned} {result.pages_scanned === 1 ? "page" : "pages"}{" "}
                        scanned
                      </p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        {Object.entries(SEVERITY).map(([key, sev]) => (
                          <span
                            key={key}
                            data-testid={`severity-count-${key}`}
                            className="rounded-full px-3.5 py-1.5 text-xs font-bold"
                            style={{ backgroundColor: `${sev.color}1f`, color: sev.color }}
                          >
                            {result.summary[key]} {sev.label}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 flex flex-wrap gap-3 border-t border-white/[0.07] pt-6">
                    <button
                      type="button"
                      data-testid="scan-pdf-button"
                      onClick={() => exportCompliancePdf(result)}
                      className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-bold text-paper/70 transition-[border-color,color] duration-200 hover:border-lilac/50 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                    >
                      <Download className="h-4 w-4" aria-hidden="true" />
                      Download PDF
                    </button>
                    <a
                      data-testid="scan-json-button"
                      href={`data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify(result, null, 2))}`}
                      download={`pepmuse-compliance-${result.grade}${result.score}.json`}
                      className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-bold text-paper/70 transition-[border-color,color] duration-200 hover:border-lilac/50 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                    >
                      <FileJson className="h-4 w-4" aria-hidden="true" />
                      Download JSON
                    </a>
                    <button
                      type="button"
                      data-testid="scan-again-button"
                      onClick={() => {
                        setResult(null);
                        setUrl("");
                        setHtml("");
                        document.getElementById("scan")?.scrollIntoView({ behavior: "smooth" });
                      }}
                      className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm font-bold text-paper/70 transition-[border-color,color] duration-200 hover:border-lilac/50 hover:text-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lilac focus-visible:ring-offset-2 focus-visible:ring-offset-card"
                    >
                      <RotateCcw className="h-4 w-4" aria-hidden="true" />
                      Scan another
                    </button>
                  </div>
                </div>
              </Reveal>

              {result.flags.length > 0 && (
                <div className="mt-8 space-y-4">
                  {(["critical", "high", "medium", "low"]).map((sev) =>
                    result.flags
                      .filter((f) => f.severity === sev)
                      .map((f, i) => (
                        <Reveal key={f.id} delay={i * 0.05}>
                          <div
                            data-testid={`flag-${f.id}`}
                            className="rounded-2xl border bg-card p-6"
                            style={{ borderColor: `${SEVERITY[sev].color}40` }}
                          >
                            <div className="flex flex-wrap items-center gap-3">
                              <ShieldAlert className="h-4.5 w-4.5 h-5 w-5" style={{ color: SEVERITY[sev].color }} aria-hidden="true" />
                              <h3 className="font-display text-lg font-extrabold text-paper">{f.title}</h3>
                              <span
                                className="rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.12em]"
                                style={{ backgroundColor: `${SEVERITY[sev].color}1f`, color: SEVERITY[sev].color }}
                              >
                                {SEVERITY[sev].label}
                              </span>
                            </div>
                            <p className="mt-3 text-sm leading-relaxed text-paper/60">{f.detail}</p>
                            {f.evidence && (
                              <p className="mt-3 rounded-xl bg-ink/70 px-4 py-2.5 font-mono text-xs text-paper/50">
                                “{f.evidence}”
                              </p>
                            )}
                            <p className="mt-3 text-sm text-paper/75">
                              <span className="font-bold text-lilac">Fix: </span>
                              {f.fix}
                            </p>
                          </div>
                        </Reveal>
                      ))
                  )}
                </div>
              )}

              {result.passed.length > 0 && (
                <Reveal>
                  <div data-testid="passed-checks" className="mt-8 rounded-3xl border border-white/[0.08] bg-card p-7">
                    <h3 className="font-display text-lg font-extrabold text-paper">Passing checks</h3>
                    <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
                      {result.passed.map((p) => (
                        <li key={p.id} className="flex items-center gap-2.5 text-sm text-paper/70">
                          <CheckCircle2 className="h-4 w-4 shrink-0 text-[#8FD8B8]" aria-hidden="true" />
                          {p.title}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              )}

              <Reveal>
                <div className="mt-8 flex flex-col items-start justify-between gap-6 rounded-[2rem] bg-gradient-to-br from-[#c8b8f0] via-lilac to-[#a78fe0] p-8 text-ink sm:flex-row sm:items-center sm:p-10">
                  <div>
                    <h3 className="font-display text-2xl font-extrabold tracking-tight">
                      Want these flags handled for you?
                    </h3>
                    <p className="mt-2 max-w-md text-sm text-ink/65">
                      {flagCount === 0
                        ? "You're in good shape. We can keep it that way as you grow."
                        : `${flagCount} ${flagCount === 1 ? "flag" : "flags"} on this scan. We build storefronts structured for research-use marketing — copy patterns, disclaimers, and documentation included.`}
                    </p>
                  </div>
                  <button
                    type="button"
                    data-testid="scan-help-cta"
                    onClick={() =>
                      onBuild({
                        need: "Website Creation",
                        idea: `Compliance scan of ${result.target}: score ${result.score} (grade ${result.grade}), ${flagCount} flags — ${result.flags
                          .map((f) => f.title)
                          .slice(0, 4)
                          .join("; ")}.`,
                      })
                    }
                    className="group inline-flex shrink-0 items-center gap-2.5 rounded-full bg-ink px-7 py-3.5 text-sm font-bold text-paper transition-[transform,box-shadow,background-color] duration-200 hover:-translate-y-0.5 hover:bg-[#2b2c3e] hover:shadow-[0_18px_36px_-12px_rgba(0,0,0,0.75)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 focus-visible:ring-offset-lilac"
                  >
                    Get help fixing this
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true" />
                  </button>
                </div>
              </Reveal>
            </div>
          )}
        </div>
      </section>

      <ClosingCTA onBuild={() => onBuild()} />
    </>
  );
}
